import { EditOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  List,
  message,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from 'antd'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet/es/Helmet'
import AccessControlAction from '../../components/AccessControlAction'
import FileItem from '../../components/Common/FileItemComponent/FileItem'
import ContentBlockWrapper from '../../components/ContentBlockWrapper'
import PageTitle from '../../components/PageTitle'
import { systemName } from '../../config'
import { ACL_ACTION_TYPE } from '../../constants'
import { formatContractDate } from '../../date_format'
import { Container } from '../../layouts/Container/Container'
import DashboardLayout from '../../layouts/DashboardLayout'
import utils from '../../utils'
import { ContractManagementRequest, mapApiContractToUi } from './contractApi'
import { CONTRACT_STATUS_MAP, formatCurrencyVN } from './mockData'

const getStatusInfo = status => {
  const normalizedStatus = String(status || '').toLowerCase()
  return (
    CONTRACT_STATUS_MAP[normalizedStatus] || {
      label: String(status || 'Khong xac dinh'),
      color: 'default',
    }
  )
}

const ContractManagementDetailPage = props => {
  const { history, match } = props
  const { contractId } = match.params

  const [contract, setContract] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const loadContractDetail = useCallback(async () => {
    try {
      setIsLoading(true)
      const { data } = await ContractManagementRequest.getContractById(
        contractId
      )
      setContract(mapApiContractToUi(data))
    } catch (error) {
      console.error(error)
      message.error(error?.vi || 'Khong tim thay hop dong')
      history.push('/utility/contracts')
    } finally {
      setIsLoading(false)
    }
  }, [contractId, history])

  useEffect(() => {
    loadContractDetail()
  }, [loadContractDetail])

  const serviceColumns = useMemo(
    () => [
      {
        title: 'STT',
        width: 60,
        align: 'center',
        render: (_, __, index) => index + 1,
      },
      {
        title: 'Loại dịch vụ',
        width: 220,
        dataIndex: 'serviceType',
      },
      {
        title: 'Giá trị',
        width: 160,
        align: 'right',
        render: record => formatCurrencyVN(record.value),
      },
      {
        title: 'Thời gian hiệu lực',
        width: 240,
        render: record =>
          `${formatContractDate(record.startDate)} đến ${formatContractDate(
            record.endDate
          )}`,
      },
      {
        title: 'Ghi chú / Quy định riêng',
        dataIndex: 'note',
      },
    ],
    []
  )

  if (!contract) return null

  const statusInfo = getStatusInfo(contract?.status)

  return (
    <DashboardLayout>
      <Helmet>
        <title>Chi tiết hợp đồng | {systemName} Portal</title>
      </Helmet>
      <PageTitle
        location={props.location}
        title={'Chi tiết hợp đồng'}
        showTitle>
        <Space>
          {/* <Button
            icon={<LeftOutlined />}
            onClick={() => history.push('/utility/contracts')}>
            Danh sách hợp đồng
          </Button> */}
          <AccessControlAction aclActionType={ACL_ACTION_TYPE.edit__CONTRACT}>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() =>
                history.push(`/utility/contracts/edit/${contract.id}`)
              }>
              Chỉnh sửa
            </Button>
          </AccessControlAction>
        </Space>
      </PageTitle>
      <ContentBlockWrapper>
        <Container maxWidth={1300}>
          <Card bordered={false} style={{ marginBottom: 16 }}>
            <Row gutter={16}>
              <Col md={8} xs={24}>
                <Statistic
                  title="Tổng giá trị hợp đồng"
                  value={contract?.totalValue || 0}
                  precision={0}
                  formatter={value => formatCurrencyVN(value)}
                />
              </Col>
              <Col md={8} xs={24}>
                <Statistic
                  title="Số dịch vụ"
                  value={
                    contract?.totalService || contract?.services?.length || 0
                  }
                />
              </Col>
              <Col md={8} xs={24}>
                <Statistic
                  title="Số tài liệu đính kèm"
                  value={
                    contract?.totalFile || contract?.documents?.length || 0
                  }
                />
              </Col>
            </Row>
            <Divider />
            <Descriptions
              title={
                <Space>
                  <Typography.Text strong>
                    {contract?.contractNo}
                  </Typography.Text>
                  <Tag color={statusInfo?.color}>{statusInfo?.label}</Tag>
                </Space>
              }
              column={2}
              bordered>
              <Descriptions.Item label="Bên A">
                {contract?.supplierName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Bên B">
                {contract.customerName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày ký kết">
                {formatContractDate(contract?.signedDate)}
              </Descriptions.Item>
              <Descriptions.Item label="Ngày hiệu lực tổng hợp đồng">
                {formatContractDate(contract?.effectiveDate)}
              </Descriptions.Item>
              <Descriptions.Item
                label="Mô tả hợp đồng / Điều khoản chung"
                span={2}>
                {contract?.description || 'Không có mô tả'}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            title="Danh sách dịch vụ thuộc hợp đồng"
            bordered={false}
            style={{ marginBottom: 16 }}>
            <Table
              rowKey={record =>
                record.id || `${record.serviceType}-${record.startDate}`
              }
              columns={serviceColumns}
              dataSource={contract?.services || []}
              pagination={false}
              scroll={{ x: 1000 }}
            />
          </Card>

          <Card title="Tài liệu đính kèm" bordered={false}>
            <List
              locale={{ emptyText: 'Chưa có tài liệu đính kèm' }}
              dataSource={contract?.documents || []}
              renderItem={file => (
                <List.Item>
                  <FileItem
                    file_id={file.fileId}
                    file_name={file.name || file.fileName || '-'}
                    file_type={utils.getExtensionFile(
                      file.name || file.fileName || ''
                    )}
                  />
                </List.Item>
              )}
            />
          </Card>
        </Container>
      </ContentBlockWrapper>
    </DashboardLayout>
  )
}

export default memo(ContractManagementDetailPage)
